import { useState, useEffect, useCallback, Fragment } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Package, TrendingUp, TrendingDown, AlertTriangle, ClipboardList,
  FileText, Truck, ArrowRightLeft, ChevronDown, ChevronRight,
  Plus, Clock, RotateCcw, Send, ScanLine, CalendarClock, FilePlus, PackageX, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '../lib/api';
import { formatDate } from '../lib/format';
import { useUrlTab, useUrlState } from '../lib/hooks/useUrlState';
import { ReturnsTab, StocktakeTab } from '../components/stores/StoresSlice1';
import { useAuth } from '../contexts/AuthContext';
import { MPRTab } from './stores/MPRTab';
import { MRTab } from './stores/MRTab';
import { ReconciliationTab } from './stores/ReconciliationTab';
import { PurchaseOrdersTab } from './stores/POTab';
import { GRNTab } from './stores/GRNTab';
import { IssuesTab } from './stores/IssuesTab';
import { canRunInspection } from './stores/stores-constants';
import { OpeningStockTab } from './stores/OpeningStockTab';
import { VendorChangeRequests } from '../components/stores/VendorChangeRequests';
import { TransfersTab } from '../components/stores/TransfersTab';
import { ReportsTab } from './stores/ReportsTab';

const API = process.env.REACT_APP_BACKEND_URL;

// Tabs for Stores module
const TABS = [
  { id: 'grn', label: 'GRN', icon: Truck },
  { id: 'po', label: 'Purchase Orders', icon: FileText },
  { id: 'mpr', label: 'MPR', icon: FilePlus },
  { id: 'mr', label: 'Material Requisitions', icon: Send },
  { id: 'issues', label: 'Material Issues', icon: ArrowRightLeft },
  { id: 'returns', label: 'Returns', icon: RotateCcw },
  { id: 'stocktake', label: 'Stocktake', icon: ScanLine },
  { id: 'transfers', label: 'Transfers', icon: Truck },
  { id: 'movements', label: 'Movements', icon: CalendarClock },
  { id: 'vcr', label: 'Vendor Requests', icon: PackageX },
  { id: 'health', label: 'Inventory Health', icon: Package },
  { id: 'opening-stock', label: 'Opening Stock / Godown', icon: FileText },
  { id: 'reconciliation', label: 'BOQ Reconciliation', icon: ClipboardList },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

export default function Stores() {
  const { activeProject } = useOutletContext() || {};
  const [activeTab, setActiveTab] = useUrlTab('tab', 'grn');
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useUrlState('project', '');
  const { user } = useAuth();

  useEffect(() => {
    apiGet('/projects')
      .then(data => setProjects(Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : [])))
      .catch(() => {});
  }, []);

  // A3 — default to the top-nav project picker's selection, but only if the
  // URL doesn't already specify one; never fights a manual change.
  useEffect(() => {
    if (!projectFilter && activeProject) setProjectFilter(activeProject);
  }, [activeProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stores & Procurement</h1>
            <p className="text-sm text-gray-500 mt-1">Manage purchase orders, GRNs, and material inventory</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={projectFilter}
              onChange={e => setProjectFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="stores-project-filter"
            >
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => changeTab(tab.id)}
                data-testid={`stores-tab-${tab.id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#3b4872] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'reconciliation' && <ReconciliationTab projectId={projectFilter} />}
        {activeTab === 'mpr' && <MPRTab projectId={projectFilter} projects={projects} />}
        {activeTab === 'po' && <PurchaseOrdersTab projectId={projectFilter} />}
        {activeTab === 'grn' && <GRNTab projectId={projectFilter} projects={projects} />}
        {activeTab === 'mr' && <MRTab projectId={projectFilter} />}
        {activeTab === 'issues' && <IssuesTab projectId={projectFilter} />}
        {activeTab === 'returns' && <ReturnsTab projectId={projectFilter} projects={projects} />}
        {activeTab === 'stocktake' && <StocktakeTab projectId={projectFilter} projects={projects} />}
        {activeTab === 'transfers' && <TransfersTab projectId={projectFilter} />}
        {activeTab === 'movements' && <MovementsTab projectId={projectFilter} />}
        {activeTab === 'vcr' && <VendorChangeRequests projectId={projectFilter} canRaise={user?.write_access === 'all' || (Array.isArray(user?.write_access) && user.write_access.includes('inventory'))} title="Vendor Requests" />}
        {activeTab === 'health' && <InventoryHealthTab projectId={projectFilter} />}
        {activeTab === 'opening-stock' && <OpeningStockTab />}
        {activeTab === 'reports' && <ReportsTab />}
      </div>
    </div>
  );
}


// ── Phase 6: Inventory Health (low-stock + overstock + dead-stock) ──
function InventoryHealthTab({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deadDays, setDeadDays] = useState(90);
  const [dead, setDead] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = projectId ? `?project_id=${projectId}` : '';
    fetch(`${API}/api/stores/dashboard${params}`, { credentials: 'include' })
      .then(r => r.json()).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (projectId) p.set('project_id', projectId);
    p.set('days', String(deadDays));
    fetch(`${API}/api/stores/dead-stock?${p.toString()}`, { credentials: 'include' })
      .then(r => r.json()).then(setDead).catch(console.error);
  }, [projectId, deadDays]);

  if (loading || !data) {
    return <div className="py-10 text-center text-gray-400 text-sm" data-testid="health-loading">Loading inventory health…</div>;
  }

  const Stat = ({ label, value, tone, Icon, testid }) => (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${tone}`}><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">{label}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="stores-inventory-health">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat testid="health-lowstock-stat" label="Low stock items" value={data.low_stock_count || 0} tone="bg-red-50 text-red-600" Icon={TrendingDown} />
        <Stat testid="health-overstock-stat" label="Overstocked items" value={data.overstock_count || 0} tone="bg-amber-50 text-amber-600" Icon={TrendingUp} />
        <Stat testid="health-deadstock-stat" label={`Dead stock (>${deadDays}d, no movement)`} value={(dead && dead.count != null) ? dead.count : (data.dead_stock_count || 0)} tone="bg-slate-100 text-slate-600" Icon={AlertTriangle} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat testid="health-pending-mr-stat" label="Pending requisitions" value={data.pending_mr ?? data.pending_indents ?? 0} tone="bg-blue-50 text-blue-600" Icon={Send} />
        <Stat testid="health-pending-returns-stat" label="Returns (last 7 days)" value={data.pending_returns ?? 0} tone="bg-purple-50 text-purple-600" Icon={RotateCcw} />
        <Card data-testid="health-last-stocktake-stat">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-teal-600"><ScanLine className="w-5 h-5" /></div>
            <div>
              <div className="text-sm font-bold text-gray-900">{data.last_stocktake ? formatDate(data.last_stocktake.date) : 'Never'}</div>
              <div className="text-xs text-gray-500">Last stocktake{data.last_stocktake ? ` · ${data.last_stocktake.items_with_variance} variance` : ''}</div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Card data-testid="health-at-risk-card" className="border-red-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> Material at Risk (below 20% of planned)</CardTitle></CardHeader>
        <CardContent>
          {(data.material_at_risk || []).length === 0
            ? <p className="text-sm text-gray-400 py-3 text-center">No critical low-stock items 🎉</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Item</th><th className="text-right">Available</th><th className="text-right">Planned</th><th className="text-right">% of plan</th></tr></thead>
              <tbody>{data.material_at_risk.map((it) => (
                <tr key={it.id} className="border-b" data-testid={`atrisk-row-${it.id}`}>
                  <td className="py-2">{it.description}</td>
                  <td className="text-right text-red-600 font-medium">{it.available} {it.unit}</td>
                  <td className="text-right text-gray-500">{it.planned}</td>
                  <td className="text-right text-red-600 font-semibold">{it.pct_of_planned}%</td>
                </tr>))}</tbody></table>}
        </CardContent>
      </Card>
      <Card data-testid="health-overdue-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-600" /> Overdue Deliveries</CardTitle></CardHeader>
        <CardContent>
          {(data.items_overdue_delivery || []).length === 0
            ? <p className="text-sm text-gray-400 py-3 text-center">No overdue deliveries</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">PO</th><th>Item</th><th className="text-right">Pending</th><th className="text-left pl-3">Due</th></tr></thead>
              <tbody>{data.items_overdue_delivery.map((it, i) => (
                <tr key={`${it.po_id}-${it.boq_id}-${i}`} className="border-b" data-testid={`overdue-row-${i}`}>
                  <td className="py-2 font-mono">{it.po_no}</td>
                  <td>{it.description}</td>
                  <td className="text-right text-amber-700 font-medium">{it.pending}</td>
                  <td className="pl-3 text-red-600">{it.delivery_date ? formatDate(it.delivery_date) : '—'}</td>
                </tr>))}</tbody></table>}
        </CardContent>
      </Card>
      <Card data-testid="health-aged-stock-card" className="border-amber-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Aged Stock (in store &gt; 90 days){data.aged_stock_value ? <span className="text-xs font-normal text-gray-500">· ₹{data.aged_stock_value.toLocaleString('en-IN')} tied up</span> : null}</CardTitle></CardHeader>
        <CardContent>
          {(data.aged_stock || []).length === 0
            ? <p className="text-sm text-gray-400 py-3 text-center">No aged stock — inventory is moving 👍</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Item</th><th>Batch</th><th className="text-right">Remaining</th><th className="text-right">Value</th><th className="text-right">Age</th></tr></thead>
              <tbody>{data.aged_stock.map((it, i) => (
                <tr key={`${it.boq_id}-${i}`} className="border-b" data-testid={`aged-row-${i}`}>
                  <td className="py-2">{it.description}</td>
                  <td className="font-mono text-xs">{it.grn_no}</td>
                  <td className="text-right">{it.remaining_qty} {it.unit}</td>
                  <td className="text-right text-amber-700 font-medium">₹{(it.value || 0).toLocaleString('en-IN')}</td>
                  <td className="text-right text-red-600 font-semibold">{it.age_days}d</td>
                </tr>))}</tbody></table>}
        </CardContent>
      </Card>
      <Card data-testid="health-lowstock-card">
        <CardHeader><CardTitle className="text-base">Low Stock</CardTitle></CardHeader>
        <CardContent>
          {(data.low_stock || []).length === 0
            ? <p className="text-sm text-gray-400 py-3 text-center">No low-stock items</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">BOQ Item</th><th className="text-right">Available</th><th className="text-right">Received</th></tr></thead>
              <tbody>{(data.low_stock).map(it => (
                <tr key={it.id} className="border-b" data-testid={`lowstock-row-${it.id}`}>
                  <td className="py-2">{it.description}</td>
                  <td className="text-right text-red-600 font-medium">{(it.available ?? 0)} {it.unit}</td>
                  <td className="text-right text-gray-500">{it.received}</td>
                </tr>))}</tbody></table>}
        </CardContent>
      </Card>
      <Card data-testid="health-overstock-card">
        <CardHeader><CardTitle className="text-base">Overstock (above max level)</CardTitle></CardHeader>
        <CardContent>
          {(data.overstock || []).length === 0
            ? <p className="text-sm text-gray-400 py-3 text-center">No overstocked items. Set max levels on items to enable this alert.</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Item</th><th>Code</th><th className="text-right">Stock</th><th className="text-right">Max</th><th className="text-right">Excess</th></tr></thead>
              <tbody>{(data.overstock).map(it => (
                <tr key={it.item_id} className="border-b" data-testid={`overstock-row-${it.item_id}`}>
                  <td className="py-2">{it.item_name}</td>
                  <td className="text-gray-500">{it.item_code}</td>
                  <td className="text-right">{it.stock} {it.unit}</td>
                  <td className="text-right text-gray-500">{it.max_level}</td>
                  <td className="text-right text-amber-600 font-medium">+{it.excess}</td>
                </tr>))}</tbody></table>}
        </CardContent>
      </Card>
      <Card data-testid="health-deadstock-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Dead Stock</CardTitle>
          <select data-testid="deadstock-days-select" value={deadDays} onChange={(e) => setDeadDays(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm">
            {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>No movement &gt; {d} days</option>)}
          </select>
        </CardHeader>
        <CardContent>
          {(!dead || (dead.items || []).length === 0)
            ? <p className="text-sm text-gray-400 py-3 text-center">No dead stock for this window</p>
            : <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Item</th><th>Code</th><th className="text-right">Stock</th><th className="text-right">Value</th><th>Last movement</th></tr></thead>
              <tbody>{dead.items.map(it => (
                <tr key={it.item_id} className="border-b" data-testid={`deadstock-row-${it.item_id}`}>
                  <td className="py-2">{it.item_name}</td>
                  <td className="text-gray-500">{it.item_code}</td>
                  <td className="text-right">{it.current_stock} {it.unit}</td>
                  <td className="text-right">₹{(it.value || 0).toLocaleString('en-IN')}</td>
                  <td className="text-gray-500">{it.last_movement_date ? formatDate(it.last_movement_date) : 'Never'}</td>
                </tr>))}</tbody></table>}
        </CardContent>
      </Card>
    </div>
  );
}


// ── B-Admin §3: Stock Movements Report ──────────────────────────────────────
function MovementsTab({ projectId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [movType, setMovType] = useState('');
  const [location, setLocation] = useState(projectId ? `project:${projectId}` : '');
  const [materialQ, setMaterialQ] = useState('');
  const [vendor, setVendor] = useState('');
  const [projects, setProjects] = useState([]);
  const [offices, setOffices] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    apiGet('/projects').then((d) => setProjects(Array.isArray(d) ? d : d?.items || d?.projects || [])).catch(() => {});
    apiGet('/config-masters/office_locations').then((d) => setOffices(d?.values || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 100 };
      if (location) params.location = location;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (movType) params.movement_type = movType;
      if (materialQ.trim()) params.material_q = materialQ.trim();
      if (vendor.trim()) params.vendor = vendor.trim();
      const data = await apiGet('/stores/movements', params);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load movements'); }
    finally { setLoading(false); }
  }, [location, page, dateFrom, dateTo, movType, materialQ, vendor]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    try {
      const p = new URLSearchParams();
      if (location) p.set('location', location);
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo) p.set('date_to', dateTo);
      if (movType) p.set('movement_type', movType);
      if (materialQ.trim()) p.set('material_q', materialQ.trim());
      if (vendor.trim()) p.set('vendor', vendor.trim());
      const tkn = localStorage.getItem('access_token');
      const res = await fetch(`${API}/api/stores/movements/export?${p}`, { headers: tkn ? { Authorization: `Bearer ${tkn}` } : {} });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `stock_movements_${dateFrom || 'all'}_${dateTo || 'all'}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(dl);
    } catch { toast.error('Excel export failed'); }
  };

  const TYPE_COLORS = {
    'GRN': 'bg-emerald-50 text-emerald-700', 'Issue': 'bg-rose-50 text-rose-700',
    'Return': 'bg-blue-50 text-blue-700', 'Transfer In': 'bg-indigo-50 text-indigo-700',
    'Transfer Out': 'bg-amber-50 text-amber-700', 'Stocktake Adj': 'bg-slate-100 text-slate-700',
  };

  return (
    <div className="space-y-4" data-testid="movements-tab">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm" data-testid="movements-date-from" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm" data-testid="movements-date-to" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
          <select value={movType} onChange={e => { setMovType(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm" data-testid="movements-type-filter">
            <option value="">All types</option>
            <option value="grn">GRN (Receipt)</option>
            <option value="issue">Issue</option>
            <option value="return">Return</option>
            <option value="transfer_in">Transfer In</option>
            <option value="transfer_out">Transfer Out</option>
            <option value="stocktake">Stocktake Adj</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
          <select value={location} onChange={e => { setLocation(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm min-w-[160px]" data-testid="movements-location-filter">
            <option value="">All locations</option>
            <option value="godown">Central Godown</option>
            {offices.map(o => <option key={o.id} value={`office:${o.id}`}>{o.name || o.id}</option>)}
            {projects.map(p => <option key={p.id} value={`project:${p.id}`}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Material</label>
          <input value={materialQ} onChange={e => { setMaterialQ(e.target.value); setPage(1); }} placeholder="Search material…"
            className="border rounded px-2 py-1.5 text-sm" data-testid="movements-material-filter" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Vendor</label>
          <input value={vendor} onChange={e => { setVendor(e.target.value); setPage(1); }} placeholder="GRN vendor…"
            className="border rounded px-2 py-1.5 text-sm" data-testid="movements-vendor-filter" />
        </div>
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="movements-refresh">Refresh</button>
        <button onClick={exportXlsx} className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] text-sm rounded flex items-center gap-1" data-testid="movements-export">Export xlsx</button>
        <span className="text-xs text-slate-500 ml-auto">{total} movements</span>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Date</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Doc No</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Type</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Location</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Material</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Qty In</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Qty Out</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Value (₹)</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">By</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">No movements found for the selected filters.</td></tr>
              : rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50" data-testid={`movement-row-${i}`}>
                  <td className="px-3 py-2 text-xs font-mono">{r.date}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.doc_no}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${TYPE_COLORS[r.type] || 'bg-slate-100 text-slate-600'}`}>{r.type}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.location}</td>
                  <td className="px-3 py-2 text-xs max-w-[180px] truncate">{r.material}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono text-emerald-700">{r.qty_in > 0 ? `+${r.qty_in}` : '—'}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono text-rose-700">{r.qty_out > 0 ? `-${r.qty_out}` : '—'}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono">₹{(r.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[100px]">{r.by}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
